<template>
  <div class="rm-screen">
    <div v-if="loading.loadNonContract" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>
    <div v-else-if="!orders.length" class="rm-empty">
      <template v-if="filters.search">{{ $t("report_mobile.no_results_for", { search: filters.search }) }}</template>
      <template v-else>
        <v-icon size="40" color="#8a817a">mdi-account-question-outline</v-icon>
        {{ $t("no_data_available") }}
      </template>
    </div>

    <template v-else>
      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("orders") }}</span>
          <span class="rm-stat__value rm-num">{{ orders.length }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("total") }}</span>
          <span class="rm-stat__value rm-num rm-nr-total">{{ grandTotal }}</span>
        </div>
      </div>

      <article v-for="order in orders" :key="order.key" class="rm-card rm-nr">
        <div class="rm-nr__head">
          <span class="rm-nr__id rm-num" :title="`ID ${order.id}`">#{{ order.id }}</span>
          <span v-if="order.when" class="rm-nr__when rm-num">{{ order.when }}</span>
        </div>
        <ul class="rm-list rm-nr__lines">
          <li v-for="line in order.lines" :key="line.key" class="rm-nr__line">
            <span class="rm-nr__qty rm-num">{{ line.quantity }}× </span>
            <span class="rm-nr__name">{{ line.name }} </span>
            <span class="rm-nr__sum rm-num">{{ line.sum }}</span>
          </li>
        </ul>
        <div class="rm-nr__foot">
          <span>{{ $t("total") }}</span>
          <b class="rm-num">{{ order.total }}</b>
        </div>
      </article>
    </template>
  </div>
</template>

<script>
import ReportMealsTabNonRegistered from "@/components/report/meals/tabs/ReportMealsTabNonRegistered.vue";
import { formatMoney } from "../../shared/formatQuantity";

/**
 * Mobilni prikaz "Neregistrovani": porudžbine neregistrovanih gostiju (isporuka bez ugovora), po jedna kartica
 * sa vremenom, obrocima i ukupnim iznosom. Pretraga (lupa u zaglavlju) traži po broju, vremenu i jelu.
 * Nasleđuje učitavanje iz ReportMealsTabNonRegistered.
 */
export default {
  name: "ReportMobileNonRegistered",
  extends: ReportMealsTabNonRegistered,
  computed: {
    locale() {
      const config = this.getCurrentLanguageConfig();
      return config.script === "latin" && config.locale === "sr-RS" ? "sr-Latn-RS" : config.locale;
    },
    currency() {
      return this.$store.state.restaurant?.settings?.price || "";
    },
    /** Porudžbine bez reda "Ukupno" koji stari tab dodaje na kraj. */
    orders() {
      const search = this.filters.search;
      const rows = this.noContractDelivery
        .filter((order) => Array.isArray(order.order_guest_transactions))
        .map((order) => {
          const lines = order.order_guest_transactions.map((line, index) => ({
            key: `${line.id || index}`,
            name: this.translate(line.meals?.name || ""),
            quantity: Number(line.quantity) || 0,
            sum: formatMoney((Number(line.price) || 0) * (Number(line.quantity) || 0), this.locale),
          }));
          const amount = order.order_guest_transactions.reduce(
            (sum, line) => sum + (Number(line.price) || 0) * (Number(line.quantity) || 0),
            0
          );
          const when = [order.date, order.delivery_time].filter(Boolean).join(" · ");
          return {
            key: String(order.id),
            id: order.id,
            when,
            lines,
            amount,
            total: this.withCurrency(amount),
            searchText: [order.id, when, ...lines.map((line) => line.name)].join(" "),
          };
        });
      return search ? this.customSearch(rows, [{ value: "searchText" }], search) : rows;
    },
    grandTotal() {
      return this.withCurrency(this.orders.reduce((sum, order) => sum + order.amount, 0));
    },
  },
  methods: {
    withCurrency(amount) {
      return [formatMoney(amount, this.locale), this.currency].filter(Boolean).join(" ");
    },
  },
};
</script>

<style lang="scss">
.rm-theme .rm-nr {
  overflow: hidden;

  &__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 12px 16px 8px;
  }

  &__id {
    font-size: 16px;
    font-weight: 700;
  }

  &__when {
    font-size: 13px;
    color: var(--rm-muted);
    text-align: right;
  }

  &__lines {
    padding: 0 16px;
    border-top: 1px solid var(--rm-divider);
  }

  &__line {
    display: flex;
    align-items: baseline;
    gap: 6px;
    padding: 9px 0;
    border-bottom: 1px solid var(--rm-divider-soft);
    font-size: 15px;
    line-height: 1.35;

    &:last-child {
      border-bottom: none;
    }
  }

  &__qty {
    flex: none;
    font-weight: 700;
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__sum {
    flex: none;
    margin-left: 8px;
    color: var(--rm-chip-text);
    white-space: nowrap;
  }

  &__foot {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 16px 12px;
    border-top: 1px solid var(--rm-divider);
    background: var(--rm-bg);
    font-size: 15px;

    b {
      font-size: 17px;
    }
  }
}

.rm-theme .rm-nr-total {
  font-size: 20px;
  overflow-wrap: anywhere;
}
</style>
